import { httpsCallable } from "firebase/functions";
import {
  getDoc,
  getDocs,
  onSnapshot,
  orderBy,
  query,
} from "firebase/firestore";
import { functions } from "../../firebase";
import {
  TV_DELIVERY_STATUSES,
  TV_PUBLICATION_STATUSES,
} from "./tvDisplayConstants";
import {
  getTvDisplayDevicePublishedCurrentDocRef,
  getTvDisplayPublicationDocRef,
  getTvDisplayPublicationItemsCollectionRef,
  getTvDisplayPublicationsCollectionRef,
} from "./tvDisplayService";

const callValidateTvDisplayPlaylistForPublish = httpsCallable(functions, "validateTvDisplayPlaylistForPublish");
const callPublishTvDisplayPlaylist = httpsCallable(functions, "publishTvDisplayPlaylist");
const callUnassignTvDisplayPlaylist = httpsCallable(functions, "unassignTvDisplayPlaylist");

export function subscribeToPublications(callback, errorCallback) {
  const q = query(getTvDisplayPublicationsCollectionRef(), orderBy("createdAt", "desc"));
  return onSnapshot(
    q,
    (snapshot) => {
      const rows = snapshot.docs.map((entry) => normalizePublication(entry));
      if (typeof callback === "function") callback(rows);
    },
    (error) => {
      if (typeof errorCallback === "function") errorCallback(error);
    }
  );
}

export function subscribeToDeviceCurrentPublication(deviceId, callback, errorCallback) {
  if (!deviceId) {
    if (typeof callback === "function") callback(null);
    return () => {};
  }

  return onSnapshot(
    getTvDisplayDevicePublishedCurrentDocRef(deviceId),
    (snapshot) => {
      if (typeof callback === "function") {
        callback(snapshot.exists() ? normalizeDeviceCurrentPublication(snapshot) : null);
      }
    },
    (error) => {
      if (typeof errorCallback === "function") errorCallback(error);
    }
  );
}

export async function validatePlaylistForPublish(data = {}) {
  const result = await callValidateTvDisplayPlaylistForPublish(data);
  return result.data || {};
}

export async function publishTvPlaylist(data = {}) {
  const result = await callPublishTvDisplayPlaylist(data);
  return result.data || {};
}

export async function unassignTvPlaylist(deviceId) {
  const result = await callUnassignTvDisplayPlaylist({ deviceId });
  return result.data || {};
}

export async function getPublicationDetails(publicationId) {
  if (!publicationId) throw new Error("Publication id is required.");
  const publicationSnapshot = await getDoc(getTvDisplayPublicationDocRef(publicationId));
  if (!publicationSnapshot.exists()) throw new Error("Publication not found.");
  const itemSnapshot = await getDocs(query(getTvDisplayPublicationItemsCollectionRef(publicationId), orderBy("order", "asc")));
  return {
    publication: normalizePublication(publicationSnapshot),
    items: itemSnapshot.docs.map((entry) => ({
      id: entry.id,
      ...(entry.data() || {}),
    })),
  };
}

export function getComputedDeliveryStatus(device = {}) {
  if (!device.lastPublishedVersion) return TV_DELIVERY_STATUSES.NEVER_PUBLISHED;
  if (device.lastSyncError) return TV_DELIVERY_STATUSES.ERROR;
  if (device.lastAppliedVersion == null) return TV_DELIVERY_STATUSES.WAITING_FOR_TV;
  if (Number(device.lastAppliedVersion) < Number(device.lastPublishedVersion || 0)) {
    return TV_DELIVERY_STATUSES.UPDATE_PENDING;
  }
  return TV_DELIVERY_STATUSES.UP_TO_DATE;
}

function normalizePublication(snapshot) {
  const data = snapshot.data() || {};
  const failures = Array.isArray(data.failures) ? data.failures : [];
  return {
    id: snapshot.id,
    publicationId: String(data.publicationId || snapshot.id || ""),
    requestId: String(data.requestId || ""),
    playlistId: String(data.playlistId || ""),
    playlistName: String(data.playlistName || ""),
    version: Math.max(0, Number(data.version || 0)),
    status: String(data.status || TV_PUBLICATION_STATUSES.BUILDING),
    scope: String(data.scope || ""),
    itemCount: Math.max(0, Number(data.itemCount || 0)),
    targetCount: Math.max(0, Number(data.targetCount || 0)),
    successfulTargetCount: Math.max(0, Number(data.successfulTargetCount || 0)),
    failedTargetCount: Math.max(0, Number(data.failedTargetCount || 0)),
    failures,
    presentation: data.presentation || null,
    sourcePlaylistUpdatedAt: data.sourcePlaylistUpdatedAt || null,
    publishedAt: data.publishedAt || null,
    createdAt: data.createdAt || null,
    updatedAt: data.updatedAt || null,
    publishedBy: data.publishedBy || null,
    createdAtMs: toMillis(data.createdAt),
    publishedAtMs: toMillis(data.publishedAt),
  };
}

function normalizeDeviceCurrentPublication(snapshot) {
  const data = snapshot.data() || {};
  return {
    id: snapshot.id,
    publicationId: data.publicationId || null,
    version: data.version == null ? null : Number(data.version || 0),
    playlistId: data.playlistId || null,
    playlistName: String(data.playlistName || ""),
    itemCount: Math.max(0, Number(data.itemCount || 0)),
    publishedAt: data.publishedAt || null,
    assignedAt: data.assignedAt || null,
    state: String(data.state || ""),
  };
}

function toMillis(value) {
  if (!value) return 0;
  if (typeof value?.toMillis === "function") return value.toMillis();
  if (typeof value?.seconds === "number") return value.seconds * 1000;
  const stamp = new Date(value).getTime();
  return Number.isFinite(stamp) ? stamp : 0;
}
